import { Phone, Mail, MapPin } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import soharLogo from "@/assets/sohar-logo-hq.webp";



const InstagramIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const FacebookIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const XIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialLinks = [
  { name: { ar: "إنستجرام", en: "Instagram" }, icon: InstagramIcon, href: "https://www.instagram.com/sohar_co/" },
  { name: { ar: "فيسبوك", en: "Facebook" }, icon: FacebookIcon, href: "https://www.facebook.com/profile.php?id=61587467127220" },
  { name: { ar: "إكس", en: "X" }, icon: XIcon, href: "https://x.com/soharsa" },
];

const Footer = () => {
  const { t, lang } = useI18n();

  const links = lang === "ar"
    ? ["الرئيسية", "مشاريعنا", "من نحن", "المميزات", "تواصل معنا"]
    : ["Home", "Projects", "About Us", "Features", "Contact"];

  const hrefs = ["#hero", "#projects", "#about", "#features", "#contact"];

  return (
    <footer className="bg-navy border-t border-cream/10 py-12 pb-24 md:pb-12">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-10">
          <div className="col-span-2 md:col-span-1">
            <img src={soharLogo} alt="Sohar" className="h-12 md:h-16 invert mb-3" />
            <p className="text-cream/70 text-xs md:text-sm leading-relaxed">{t("footer.desc")}</p>
          </div>

          <div>
            <h3 className="text-cream font-bold mb-4 text-base">{t("footer.quickLinks")}</h3>
            <div className="flex flex-col gap-2">
              {links.map((link, i) => (
                <a key={link} href={hrefs[i]} className="text-cream/70 hover:text-cream transition-colors text-sm">
                  {link}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-cream font-bold mb-4 text-base">{t("footer.contactUs")}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-cream/70">
                <Phone size={14} className="text-cream" />
                <span dir="ltr">+966 11 500 1577</span>
              </div>
              <div className="flex items-center gap-2 text-cream/70">
                <Mail size={14} className="text-cream" />
                <span>info@sohar.sa</span>
              </div>
              <div className="flex items-center gap-2 text-cream/70">
                <MapPin size={14} className="text-cream" />
                <span>{lang === "ar" ? "القيروان، الرياض" : "Al Qairawan, Riyadh"}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-cream font-bold mb-4 text-base">{lang === "ar" ? "تابعنا" : "Follow Us"}</h3>
            <div className="flex flex-col gap-3">
              {socialLinks.map((social) =>
                social.href ? (
                  <a
                    key={social.name.en}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-cream/70 hover:text-cream transition-colors text-sm group"
                  >
                    <social.icon size={20} />
                    <span>{lang === "ar" ? social.name.ar : social.name.en}</span>
                  </a>
                ) : (
                  <span
                    key={social.name.en}
                    className="flex items-center gap-3 text-cream/70 text-sm"
                  >
                    <social.icon size={20} />
                    <span>{lang === "ar" ? social.name.ar : social.name.en}</span>
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-cream/10 pt-6 pb-10 flex flex-col md:flex-row items-center justify-between gap-4 text-cream/70 text-sm">
          <span>{t("footer.rights")}</span>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-cream/40 hover:text-cream/70 transition-colors">
              {"\n"}
            </a>
            <a href="/terms" className="text-cream/40 hover:text-cream/70 transition-colors">
              {"\n"}
            </a>
            <a href="/connect" className="text-cream/40 hover:text-cream/70 transition-colors">
              {"\n"}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
